// src/components/PeriodSearchFilter.js
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import filterIcon from "../assets/filter-icon.png";
import CustomInput from "./CustomInput";
import Dropdown from "./Dropdown";
import CustomButton from "./CustomButton";

// assets
import searchIcon from "../assets/search_icon.png";
import FilterButton from "./FilterButton";

const PeriodSearchFilter = ({
  options,
  selected,
  setSelected,
  thick,
  errorText,
  onChange,
  nameInAr = false,
  searchQuery,
  setSearchQuery,
  selectedMonth,
  setSelectedMonth,
  selectedYear,
  setSelectedYear,
  withoutFilterButton = false,
  handleSearch = () => {},
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const { t } = useTranslation("general");

  // Generate month options
  const monthOptions = [
    { value: 0, name: t("january") },
    { value: 1, name: t("february") },
    { value: 2, name: t("march") },
    { value: 3, name: t("april") },
    { value: 4, name: t("may") },
    { value: 5, name: t("june") },
    { value: 6, name: t("july") },
    { value: 7, name: t("august") },
    { value: 8, name: t("september") },
    { value: 9, name: t("october") },
    { value: 10, name: t("november") },
    { value: 11, name: t("december") },
    { value: "", name: t("selectAll") },
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => ({
    value: String(currentYear - i),
    name: String(currentYear - i),
  }));

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleItemClick = (item) => {
    setSelected(item);
    if (onChange) {
      onChange(item);
    }
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      toggleDropdown();
    }
  };

  const handleInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleResetSearch = () => {
    setSearchQuery("");
  };

  const showError = !selected?.value && errorText;

  useEffect(() => {
    const currentMonth = new Date().getMonth();
    setSelectedMonth(monthOptions[currentMonth]);
  }, []);

  return (
    <>
      <div className="o-column o-column--vertical-align_center o-column--vertical-align_start o-row--gap-size_500 c-search-container o-column--horizontal-align_space-between ">
        {showError && <div className="error-message">{t(errorText)}</div>}

        <section className="u-width_full-width o-column o-column--gap-size_100">
          <label className="t-bold_bold">{`${t("searchTitle")} ${t(
            selected?.title
          )}`}</label>
          <div className="o-row o-row--gap-size_400">
            {selected?.type === "dropdown" ? (
              <Dropdown
                options={selected?.dropdown?.data}
                selected={searchQuery || selected?.dropdown?.data[0]}
                setSelected={setSearchQuery}
                thick
                flex
              />
            ) : selected?.type === "money" ? (
              <CustomInput
                type="money"
                placeholder={`${t("searchTitle")} ${t(selected?.title)}`}
                flex
                value={searchQuery}
                onChange={handleInputChange}
                resetSearch={handleResetSearch}
                ref={inputRef}
                thick
              />
            ) : (
              <CustomInput
                placeholder={`${t("searchTitle")} ${t(selected?.title)}`}
                flex
                value={searchQuery}
                onChange={handleInputChange}
                resetSearch={handleResetSearch}
                ref={inputRef}
                thick
              />
            )}

            {!withoutFilterButton && (
              <div
                className="c-search-filter"
                ref={dropdownRef}
                onClick={toggleDropdown}
                onKeyDown={handleKeyDown}
              >
                <div
                  tabIndex={0}
                  className={`toggle ${isOpen ? "opened" : ""} ${
                    thick ? "thick" : ""
                  }`}
                >
                  <FilterButton t={t} isFiltersOpen={!isOpen}/>
                </div>

                {isOpen && (
                  <>
                    <ul className="filter-menu">
                      {Array.isArray(options) &&
                        options.map((option, index) => (
                          <li
                            key={index}
                            className="filter-menu-item"
                            onClick={() => handleItemClick(option)}
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                handleItemClick(option);
                              }
                            }}
                          >
                            {!nameInAr
                              ? typeof option === "object"
                                ? t(option?.title) || option?.name
                                : option
                              : typeof option === "object"
                              ? t(option?.title) || option?.nameInAr
                              : option}
                          </li>
                        ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        </section>
        <div className="o-row o-row--gap-size_300 multiple-search-filter o-column-row-switcher u-width_full-width">
          {/* Month and Year Dropdowns */}
          <div className="o-row o-row--gap-size_300 o-column-row-switcher u-width_full-width">
            <div className="o-column o-column--gap-size_100">
              <span className="bold-text">{t("month")}</span>
              <Dropdown
                options={monthOptions}
                selected={selectedMonth}
                setSelected={setSelectedMonth}
                thick
                flex
                title={t("month")}
              />
            </div>

            <div className="o-column o-column--gap-size_100">
              <span className="bold-text">{t("year")}</span>
              <Dropdown
                options={yearOptions}
                selected={selectedYear}
                setSelected={setSelectedYear}
                thick
                flex
                title={t("year")}
              />
            </div>
          </div>
          <CustomButton
            title={t("search")}
            onClick={handleSearch}
            icon={searchIcon}
            iconSize={15}
            classList="c-search-button"
          />
        </div>
      </div>
    </>
  );
};

export default PeriodSearchFilter;
